import React, { useMemo } from 'react';
import { Box, Flex, Typography } from '@fullstory/components';
import { getSingleNumberValueText } from '../../utils/singleNumber';
import { Display } from '@fullstory/proto/fs/services/webber/frontend/display';
import { TimeRange } from '@fullstory/proto/fs/api/segment';
import { ChangePill } from '../ChangePill';
import { TimeRangeComparison } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { changePercentageDisplayToFormatNumberOptions } from '../../utils';
import {
  getChangeFromCompareText,
  getCompareText,
  getDateDisplayString,
  toDateRangeComparison,
} from '../../utils/comparison';
import { intl } from '@fullstory/intl';

export interface SingleNumberValueProps {
  /** The value (metric) displayed. */
  value?: number;
  prevValue?: number;
  inverse?: boolean;
  invertColors?: boolean;
  large?: boolean;
  bold?: boolean;
  displayFormat?: Display;
  timeRange?: TimeRange;
  pastRange?: TimeRangeComparison | null;
  description?: string;
  comparisonEnabled?: boolean;
}
export const SingleNumberValue: React.FC<SingleNumberValueProps> = function SingleNumberValue({
  value,
  prevValue,
  inverse = false,
  invertColors = false,
  large = false,
  bold = false,
  displayFormat,
  description,
  timeRange,
  pastRange,
  comparisonEnabled = false,
}) {
  const textColor = inverse ? 'neutrals-n0' : 'neutrals-n10';
  const valueText = getSingleNumberValueText(value, displayFormat);
  const prevValueText = getSingleNumberValueText(prevValue, displayFormat);
  const fontSize = large ? '100px' : '32px';

  const timeRangeString = timeRange
    ? getDateDisplayString(timeRange.startTime, timeRange.endTime)
    : intl.formatMessage({ defaultMessage: 'Current ', id: 'dDnL4L' });

  const now = useMemo(() => new Date(), []);
  const dateRangeComparison = toDateRangeComparison(pastRange ?? null, now);
  const previousRangeString = dateRangeComparison
    ? getCompareText(dateRangeComparison, true)
    : intl.formatMessage({ defaultMessage: 'Previous period', id: 'hCCWuZ' });

  const changeFromPreviousRange = dateRangeComparison
    ? getChangeFromCompareText(dateRangeComparison)
    : intl.formatMessage({ defaultMessage: 'Change from previous period:', id: 'LFdTuS' });

  return (
    <Flex
      data-component="SingleNumberValue"
      column
      justifyContent={comparisonEnabled ? 'space-around' : 'flex-start'}
      style={{ height: '100%' }}
    >
      {description && (
        <Box marginTop={!comparisonEnabled ? 32 : 0}>
          <Typography variant="heading2" color={textColor}>
            {description}
          </Typography>
        </Box>
      )}
      {comparisonEnabled ? (
        <>
          <Flex justifyContent="space-around" style={{ width: '100%' }}>
            <Flex column>
              <Typography variant="body2" fontSize={48} color={textColor} testId="value-text">
                {valueText}
              </Typography>
              <Box marginTop={12}>
                <Typography variant="body2" color={textColor} testId="value-range">
                  {timeRangeString}
                </Typography>
              </Box>
            </Flex>
            <Flex column>
              <Typography
                variant="body2"
                fontSize={48}
                color={textColor}
                textAlign="right"
                testId="prev-value-text"
              >
                {prevValueText}
              </Typography>
              <Box marginTop={12}>
                <Typography variant="body2" color={textColor} testId="prev-value-range">
                  {previousRangeString}
                </Typography>
              </Box>
            </Flex>
          </Flex>
          <Flex justifyContent="center" alignItems="center">
            <Box marginBottom={4}>
              <Typography variant="body2" color={textColor} testId="change-from-range">
                {changeFromPreviousRange}
              </Typography>
            </Box>
            <Box marginLeft={8} inline>
              <ChangePill
                value={value}
                prevValue={prevValue}
                numberFormatOptions={changePercentageDisplayToFormatNumberOptions(displayFormat)}
                invertColors={invertColors}
              />
            </Box>
          </Flex>
        </>
      ) : (
        <Box marginTop={description ? (!comparisonEnabled ? 32 : 64) : 0}>
          <Typography
            variant={bold ? 'title1' : 'body3'}
            fontSize={fontSize}
            lineHeight="1em"
            color={textColor}
            textAlign="right"
            testId="value-text"
          >
            {valueText}
          </Typography>
        </Box>
      )}
    </Flex>
  );
};
